import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import {
  CALL_OUTCOMES,
  INTERACTIONS,
  recordInteraction,
  type CallOutcome,
  type Interaction,
} from './detail-api';

/**
 * "O que aconteceu?" — and, in the same breath, "o que vem depois?".
 *
 * Six ways to record an interaction, and one form underneath that changes with the choice. The
 * follow-up is part of that form rather than a second screen, because recording a call and deciding
 * to call again are one thought: a CRM where they are two screens is a CRM where the second one does
 * not happen, and a pipeline full of leads nobody has a next step for is the commonest way one dies.
 *
 * A call additionally asks how it went. The pattern is the information — somebody who has not
 * answered four times is not a lead who needs a fifth call, they are a lead who needs an e-mail, and
 * a bare "ligou" cannot tell anybody that.
 */

const ICONS: Record<Interaction, string> = {
  NOTE: '📝',
  CALL: '📞',
  WHATSAPP: '💬',
  EMAIL: '✉️',
  SMS: '📱',
  VISIT: '📍',
};

export function InteractionBar({ leadId }: { leadId: string }): React.JSX.Element {
  const client = useQueryClient();

  const [type, setType] = useState<Interaction | null>(null);
  const [body, setBody] = useState('');
  const [outcome, setOutcome] = useState<CallOutcome>('ANSWERED');
  const [withFollowUp, setWithFollowUp] = useState(true);
  const [followUpTitle, setFollowUpTitle] = useState('');
  const [dueInDays, setDueInDays] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () =>
      recordInteraction(leadId, {
        type: type!,
        body: body.trim(),
        ...(type === 'CALL' ? { outcome } : {}),
        ...(withFollowUp && followUpTitle.trim()
          ? { followUp: { title: followUpTitle.trim(), dueInDays } }
          : {}),
      }),
    onSuccess: () => {
      setType(null);
      setBody('');
      setFollowUpTitle('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['timeline'] });
      void client.invalidateQueries({ queryKey: ['lead-workspace', leadId] });
      void client.invalidateQueries({ queryKey: ['related-tasks'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  function choose(next: Interaction): void {
    setType((current) => (current === next ? null : next));
    setError(null);
    // A sensible next step for what just happened, which somebody can overwrite.
    setFollowUpTitle(t(`interaction.followUp.${next}`));
  }

  return (
    <section className="interactions">
      <p className="interactions__prompt">{t('interaction.prompt')}</p>

      <div className="interactions__choices">
        {INTERACTIONS.map((entry) => (
          <button
            key={entry}
            type="button"
            className={`interaction${type === entry ? ' is-active' : ''}`}
            onClick={() => choose(entry)}
          >
            <span className="interaction__icon" aria-hidden="true">
              {ICONS[entry]}
            </span>
            <span className="interaction__label">{t(`interaction.${entry}`)}</span>
          </button>
        ))}
      </div>

      {type && (
        <form
          className="stack interactions__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (body.trim()) record.mutate();
          }}
        >
          <label className="field">
            <span>{t(`interaction.body.${type}`)}</span>
            <textarea
              rows={3}
              value={body}
              autoFocus
              placeholder={t(`interaction.placeholder.${type}`)}
              onChange={(event) => setBody(event.target.value)}
            />
          </label>

          {type === 'CALL' && (
            <label className="field">
              <span>{t('interaction.outcome')}</span>
              <select
                className="select"
                value={outcome}
                onChange={(event) => setOutcome(event.target.value as CallOutcome)}
              >
                {CALL_OUTCOMES.map((each) => (
                  <option key={each} value={each}>
                    {t(`interaction.outcome.${each}`)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="interactions__followup">
            <label className="prefs__switch">
              <input
                type="checkbox"
                checked={withFollowUp}
                onChange={(event) => setWithFollowUp(event.target.checked)}
              />
              <span className="prefs__switch-text">
                <span className="strong">{t('interaction.scheduleNext')}</span>
                <span className="muted small">{t('interaction.scheduleNextHint')}</span>
              </span>
            </label>

            {withFollowUp && (
              <div className="form-grid">
                <label className="field">
                  <span>{t('interaction.followUpTitle')}</span>
                  <input
                    value={followUpTitle}
                    onChange={(event) => setFollowUpTitle(event.target.value)}
                    maxLength={300}
                  />
                </label>

                <label className="field">
                  <span>{t('interaction.when')}</span>
                  <select
                    className="select"
                    value={dueInDays}
                    onChange={(event) => setDueInDays(Number(event.target.value))}
                  >
                    {[0, 1, 2, 3, 7, 15, 30].map((days) => (
                      <option key={days} value={days}>
                        {t(`interaction.in.${days}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}

          <div className="cell-inline">
            <button
              type="submit"
              className="button button--primary"
              disabled={body.trim() === '' || record.isPending}
            >
              {record.isPending ? t('common.saving') : t('interaction.save')}
            </button>

            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setType(null)}
            >
              {t('confirm.cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
